import { useEffect, useState } from "react";

import { useTimezone } from "@web-app/api";
import { Avatar } from "@web-app/components";

import { ChatMessage } from "../../api/services/chat";

interface CallMessageBubbleProps {
  currentUserId: string;
  currentUserImage?: string | null;
  currentUserName: string;
  isOwnMessage: boolean;
  message: ChatMessage;
  otherPartyImage?: string;
  otherPartyName?: string;
}

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }
  return `${minutes}:${String(secs).padStart(2, "0")}`;
}

function LiveDurationTimer({ startTime }: { startTime: string }) {
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const start = new Date(startTime).getTime();
    const updateDuration = () => {
      setDuration(Math.floor((Date.now() - start) / 1000));
    };

    updateDuration();
    const interval = setInterval(updateDuration, 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  return <span className="font-medium">{formatDuration(duration)}</span>;
}

export default function CallMessageBubble({
  message,
  isOwnMessage,
  currentUserId,
  currentUserImage,
  currentUserName,
  otherPartyImage,
  otherPartyName = "User",
}: CallMessageBubbleProps) {
  const { userTimezone, convertToTimezone } = useTimezone();
  const isOngoing = message.callStatus === "ONGOING";
  const isEnded = message.callStatus === "ENDED";
  const userJoinedCall = message.callParticipants?.some(
    (p) => p.userId === currentUserId,
  );
  const isMissedCall = isEnded && !isOwnMessage && !userJoinedCall;

  const displayParticipants = message.callParticipants || [];

  return (
    <div
      className={`flex gap-1.5 lg:gap-2 ${isOwnMessage ? "justify-end" : "justify-start"}`}
    >
      {!isOwnMessage && (
        <Avatar image={otherPartyImage || ""} name={otherPartyName} size={6} />
      )}
      <div className="flex items-center gap-2 lg:gap-3 px-3 py-2 lg:px-4 lg:py-3 bg-white border border-gray-200 rounded-xl shadow-sm">
        <div
          className={`w-6 h-6 lg:w-8 lg:h-8 rounded-full flex items-center justify-center ${
            isOngoing
              ? "bg-green-100"
              : isMissedCall
                ? "bg-red-100"
                : "bg-gray-100"
          }`}
        >
          <svg
            className={`w-3 h-3 lg:w-4 lg:h-4 ${
              isOngoing
                ? "text-green-600"
                : isMissedCall
                  ? "text-red-600"
                  : "text-gray-600"
            }`}
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 00-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z" />
          </svg>
        </div>

        <div className="flex flex-col">
          <div className="flex items-center gap-1.5 lg:gap-2">
            <span
              className={`text-xs font-medium ${isMissedCall ? "text-red-600" : "text-gray-900"}`}
            >
              {isOngoing
                ? "Ongoing call"
                : isMissedCall
                  ? "Missed Call"
                  : "Call Ended"}
              {isEnded &&
                !isMissedCall &&
                message.callDuration !== undefined &&
                ` - ${formatDuration(message.callDuration)}`}
            </span>
            {isOngoing && message.callStartedAt && (
              <span className="inline-flex items-center px-1.5 py-0.5 lg:px-2 rounded-full text-[10px] lg:text-xs bg-green-100 text-green-700">
                <span className="w-1 h-1 lg:w-1.5 lg:h-1.5 bg-green-500 rounded-full mr-1 lg:mr-1.5 animate-pulse" />
                <LiveDurationTimer startTime={message.callStartedAt} />
              </span>
            )}
          </div>
          <span className="text-[10px] lg:text-xs text-gray-500">
            {convertToTimezone(
              new Date(message.createdAt),
              userTimezone,
            ).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        </div>

        {isEnded && displayParticipants.length > 0 && (
          <div className="flex items-center -space-x-1.5 lg:-space-x-2">
            {displayParticipants.slice(0, 3).map((participant) => (
              <div
                key={participant.userId}
                className="ring-2 ring-white rounded-full"
                title={participant.name}
              >
                <Avatar
                  image={participant.image}
                  name={participant.name}
                  size={6}
                />
              </div>
            ))}
            {displayParticipants.length > 3 && (
              <div
                className="w-6 h-6 lg:w-8 lg:h-8 rounded-full bg-gray-200 ring-2 ring-white flex items-center justify-center text-[10px] lg:text-xs font-medium text-gray-600 cursor-default"
                title={displayParticipants.map((p) => p.name).join(", ")}
              >
                +{displayParticipants.length - 3}
              </div>
            )}
          </div>
        )}
      </div>
      {isOwnMessage && (
        <Avatar
          image={currentUserImage || ""}
          name={currentUserName}
          size={8}
        />
      )}
    </div>
  );
}
